import { useState, useCallback, memo, useEffect, useRef } from 'react';
import {
    ReactFlow,
    Controls,
    Background,
    applyNodeChanges,
    applyEdgeChanges,
    MiniMap,
    Panel,
    addEdge,
    Handle,
    Position,
    type Node,
    type Edge,
    getOutgoers,
    useNodesState,
    useEdgesState,
    useReactFlow,
    ReactFlowProvider,
    useHandleConnections,
    Connection,
    OnNodesChange,
    OnEdgesChange,
    OnConnectEnd
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Container from '../../../components/Container';
import api from '../../../utils/axios';
import { IChatbotQuestion, ITranslation, TLang } from 'sierra';
import { useApp } from '../../../contexts/AppContext';
import { Box, Button, CircularProgress, Grid2, IconButton, Modal, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';

interface InputNodeData extends Record<string, unknown> {
    id: string;
    label: ITranslation; // Define 'label' as required by your use case
    is_question: boolean;
    onLabelChange: (id: string, label: string, lang: TLang) => void;
    onDelete: () => void;
}

type InputNode = Node<InputNodeData>;

const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    // border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const InputNode = memo(({ data, isConnectable }: { data: InputNodeData, isConnectable?: boolean }) => {
    const { lang } = useApp()
    const sourceConnections = useHandleConnections({ type: 'source' });
    const targetConnections = useHandleConnections({ type: 'target' });

    return (
        <div style={{ backgroundColor: data.is_question ? '#92e7f0' : '#98d99f', padding: 10, borderRadius: 10, paddingTop: 20 }}>
            {data.id !== "1" &&
                <Handle
                    type="target"
                    position={Position.Top}
                    // onConnect={(params) => console.log('handle onConnect', params)}
                    isConnectable={targetConnections.length < 1}
                />}
            <textarea
                cols={30}
                rows={3}
                draggable={false}
                onChange={(e) => data.onLabelChange(data.id, e.target.value, lang)}
                value={data.label[lang] || ''}
            />
            <Handle
                type="source"
                position={Position.Bottom}
                isConnectable={data.is_question ? sourceConnections.length < 1 : isConnectable}
            />
            {data.id !== "1" &&
                <IconButton onClick={() => data.onDelete && data.onDelete()} sx={{ position: 'absolute', top: 5, right: 7, padding: '1px' }}>
                    <Close style={{ fontSize: 12 }} />
                </IconButton>
            }
        </div>
    )
})

// const initialNodes = [
//     {
//         id: '1',
//         data: { label: 'Hello' },
//         position: { x: 0, y: 0 },
//         type: 'inputNode',
//         deletable: false
//     },
//     {
//         id: '2',
//         data: { label: 'World' },
//         position: { x: 100, y: 100 },
//         type: 'inputNode',
//     },
// ];

// const initialEdges = [
//     { id: '1-2', source: '1', target: '2' },
// ];

const nodeTypes = {
    inputNode: InputNode,
};

const ReactFlowContainer = () => {
    const { screenToFlowPosition } = useReactFlow();
    const { translate, screenSize } = useApp()

    const [nodes, setNodes] = useNodesState<InputNode>([]);
    const [edges, setEdges] = useEdgesState<Edge>([]);
    const [saving, setSaving] = useState(false);
    const [deleteNode, setDeleteNode] = useState<null | string>(null);

    useEffect(() => {
        fetchQuestions()
    }, [])

    const fetchQuestions = () => {
        api.get('/chatbot/questions').then(({ data }: { data: IChatbotQuestion[] }) => {
            setNodes(data.map(q => ({
                id: q.question_id.toString(),
                data: { id: q.question_id.toString(), label: q.message, is_question: q.is_question, onLabelChange: onLabelChange, onDelete: () => setDeleteNode(q.question_id.toString()) },
                position: { x: q.position_x, y: q.position_y },
                type: 'inputNode'
            })));
            setEdges(data.filter(item => item.parent_id !== null) // Only include items with a parent
                .map(item => ({
                    id: `${item.parent_id}-${item.question_id}`, // Unique edge ID
                    source: item.parent_id.toString(), // Source is the parent_id
                    target: item.question_id.toString() // Target is the question_id
                }))
            );
        }).catch(console.error)
    }

    const CreateOrUpdateQuestion = (question_id: string, parent_id: string, message: ITranslation, position_x: number, position_y: number, is_question: boolean) => {
        setSaving(true)
        delete message.id;
        api.put('/chatbot/question/' + question_id, { parent_id, message, position_x: Math.round(position_x), position_y: Math.round(position_y), is_question })
            .catch((err) => {
                console.error(err)
                fetchQuestions()
            }).finally(() => setSaving(false))
    }

    const UpdateQuestionPosition = (question_id: string, position_x: number, position_y: number) => {
        setSaving(true)
        api.patch('/chatbot/question/' + question_id + '/updatePosition', { position_x: Math.round(position_x), position_y: Math.round(position_y) })
            .catch((err) => {
                console.error(err)
                fetchQuestions()
            }).finally(() => setSaving(false))
    }

    const updateMessageTimeouts = useRef<Record<string, number>>({})

    const UpdateQuestionMessage = (question_id: string, message: ITranslation) => {
        clearTimeout(updateMessageTimeouts.current[question_id])
        updateMessageTimeouts.current[question_id] = setTimeout(() => {
            setSaving(true)
            delete message.id;
            api.patch('/chatbot/question/' + question_id + '/updateMessage', { message })
                .catch((err) => {
                    console.error(err)
                    fetchQuestions()
                }).finally(() => setSaving(false))
        }, 1000);
    }

    const DeleteQuestion = (question_id: string) => {
        setSaving(true)
        api.delete('/chatbot/question/' + question_id)
            .catch((err) => {
                console.error(err)
                fetchQuestions()
            }).finally(() => setSaving(false))
    }

    // const addNode = () => {
    //     const id = nodes.reduce((max, node) => Number(node.id) > max ? Number(node.id) : max, 0) + 1;
    //     const newNode = {
    //         id: id.toString(),
    //         data: { id: id.toString(), label: `Node ${id}`, onChange: onLabelChange },
    //         position: { x: Math.random() * 300, y: Math.random() * 300 },
    //         type: 'inputNode',
    //     };
    //     setNodes((nds) => [...nds, newNode]);
    // };

    // const onReconnect = useCallback(
    //     (oldEdge, newConnection) => {
    //         // console.log('onReconnect', oldEdge, newConnection)
    //         setEdges((els) => reconnectEdge(oldEdge, newConnection, els))
    //         const node = nodes.find(n => n.id === newConnection.target)
    //         CreateOrUpdateQuestion(
    //             newConnection.target,
    //             newConnection.source,
    //             node.data.label,
    //             node.position.x,
    //             node.position.y
    //         )
    //     },
    //     [nodes],
    // );

    const onConnect = useCallback(
        (params: Connection) => {
            // console.log('onConnect', params)
            setEdges((eds) => addEdge(params, eds))
            // const node = nodes.find(n => n.id === params.target)
            // CreateOrUpdateQuestion(
            //     params.target,
            //     params.source,
            //     node.data.label,
            //     node.position.x,
            //     node.position.y
            // )
        },
        [nodes],
    );

    const onNodesChange: OnNodesChange = useCallback(
        (changes) => {
            // console.log('onNodesChange', changes)
            setNodes((nds) => applyNodeChanges(changes, nds) as InputNode[])
            changes.forEach(change => {
                if (change.type === 'position' && change.dragging === false && change.position) {
                    UpdateQuestionPosition(change.id, change.position.x, change.position.y)
                }
            })
        },
        [],
    );

    const getAllOutgoers = (node: InputNode, nodes: InputNode[], edges: Edge[], visited = new Set()): InputNode[] => {
        if (visited.has(node.id)) {
            return []; // Avoid infinite loop for cyclic graphs
        }

        // Mark the current node as visited
        visited.add(node.id);

        // Get direct outgoers of the current node
        const outgoers = getOutgoers(node, nodes, edges);

        // Recursively get outgoers for each outgoer
        const allOutgoers = outgoers.flatMap(outgoer => getAllOutgoers(outgoer, nodes, edges, visited));

        // Combine the current outgoers with the recursively collected outgoers
        return [...outgoers, ...allOutgoers];
    };

    const handleDeleteNode = useCallback((id: string) => {
        // console.log('onNodesDelete', deleted, getOutgoers(deleted[0], nodes, edges))
        DeleteQuestion(id)
        const node = nodes.find(node => node.id === id);
        if (!node) {
            console.warn('Tried to delete a node that does not exist');
            return nodes;
        }
        const outgoers = getAllOutgoers(node, nodes, edges);
        setNodes(nodes => {
            return nodes.filter(n => n.id !== node.id && !outgoers.find(out => n.id === out.id));
        })
        setEdges((eds) => eds.filter((edge) => edge.source !== id && edge.target !== id && !outgoers.find(out => edge.source === out.id || edge.target === out.id)));
        // setEdges(
        //     deleted.reduce((acc, node) => {
        //         const incomers = getIncomers(node, nodes, edges);
        //         const outgoers = getOutgoers(node, nodes, edges);
        //         const connectedEdges = getConnectedEdges([node], edges);

        //         const remainingEdges = acc.filter(
        //             (edge) => !connectedEdges.includes(edge),
        //         );

        //         const createdEdges = incomers.flatMap(({ id: source }) =>
        //             outgoers.map(({ id: target }) => ({
        //                 id: `${source}->${target}`,
        //                 source,
        //                 target,
        //             })),
        //         );

        //         return [...remainingEdges, ...createdEdges];
        //     }, edges),
        // );
    }, [nodes, edges]);

    const onEdgesChange: OnEdgesChange = useCallback(
        (changes) => {
            // console.log('onEdgesChange', changes)
            setEdges((eds) => applyEdgeChanges(changes, eds))
        },
        [],
    );

    const onLabelChange = (id: string, newLabel: string, lang: TLang) => {
        // setNodes((nds) =>
        //     nds.map((node) => {
        //         if (id === node.id) {
        //             const newNode = { ...node };
        //             newNode.data.label[lang] = newLabel;
        //             console.log(newNode.data)
        //             UpdateQuestionMessage(id, newNode.data.label)
        //             return newNode;
        //         } else return node;
        //     })
        // )

        setNodes((nds) => {
            const updatedNode = nds.find(n => n.id === id)
            if (!updatedNode) {
                console.warn('Tried to update a node that does not exist');
                return nds;
            }
            updatedNode.data.label[lang] = newLabel;
            UpdateQuestionMessage(id, updatedNode.data.label)
            return nds.map((node) => {
                if (id === node.id) return { ...node, data: { ...node.data, label: { ...node.data.label, [lang]: newLabel }, }, };
                else return node;
            })
        });
    }

    const onConnectEnd: OnConnectEnd = useCallback(
        (event, connectionState) => {
            // when a connection is dropped on the pane it's not valid
            if (!connectionState.isValid && connectionState.fromNode) {
                // we need to remove the wrapper bounds, in order to get the correct position
                const id = (nodes.reduce((max, node) => Number(node.id) > max ? Number(node.id) : max, 0) + 1).toString();
                const fromNode = connectionState.fromNode
                // const id = getId();
                const { clientX, clientY } =
                    'changedTouches' in event ? event.changedTouches[0] : event;
                const newNode: InputNode = {
                    id,
                    position: screenToFlowPosition({
                        x: clientX,
                        y: clientY,
                    }),
                    data: { id, label: { en: `Node ${id}`, ar: `Node ${id}`, zh: `Node ${id}` }, is_question: fromNode.data.is_question ? false : true, onLabelChange: onLabelChange, onDelete: () => setDeleteNode(id) },
                    type: 'inputNode',
                    origin: [0.5, 0.0]
                };

                // console.log('new node: ' + JSON.stringify(newNode), 'from node:', JSON.stringify(fromNode))

                setNodes((nds) => nds.concat(newNode));
                setEdges((eds) => eds.concat({ id, source: fromNode.id as string, target: id }));

                CreateOrUpdateQuestion(
                    newNode.id,
                    fromNode.id,
                    newNode.data.label,
                    newNode.position.x,
                    newNode.position.y,
                    newNode.data.is_question
                )
            }
        },
        [screenToFlowPosition, nodes],
    );

    return (
        <Container >
            <ReactFlow
                nodes={nodes}
                nodeTypes={nodeTypes}
                edges={edges}
                onEdgesChange={onEdgesChange}
                // onNodesDelete={onNodesDelete}
                onNodesChange={onNodesChange}
                onConnect={onConnect}
                // onReconnect={onReconnect}
                onConnectEnd={onConnectEnd}
                fitView
                fitViewOptions={{ padding: 2 }}
                nodeOrigin={[0.5, 0]}
                onBeforeDelete={() => Promise.resolve(false)}
            >
                <Panel position="bottom-left" style={{ paddingLeft: 50 }}>
                    <p><span style={{ color: '#77a87d' }}>{translate("Green")}</span> {translate("represents messages/answers")}</p>
                    <p><span style={{ color: '#5ab6bf' }}>{translate("Blue")}</span> {translate("represents questions")}</p>
                    <p>{translate("Drag the handles to create new nodes")}</p>
                </Panel>
                <Panel position="top-right">
                    {saving && <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <p>{translate("Saving")}</p>
                        <CircularProgress size={18} />
                    </div>}
                </Panel>
                <Background />
                <Controls />
                {!screenSize.xs && <MiniMap />}
            </ReactFlow>

            <Modal
                open={deleteNode ? true : false}
                onClose={() => setDeleteNode(null)}
            >
                <Box sx={ModalStyle}>
                    <Grid2 container justifyContent={'center'} gap={2}>
                        <Grid2>
                            <Typography>
                                {translate("Are you sure you want to delete this node? This action is not reversible and will delete all child nodes.")}
                            </Typography>
                        </Grid2>
                        <Grid2 container gap={2}>
                            <Grid2>
                                <Button size='small' variant='contained' color='error' onClick={() => {
                                    setDeleteNode(null);
                                    if (!deleteNode) return;
                                    handleDeleteNode(deleteNode)
                                    setDeleteNode(null)
                                }}>{translate('Confirm')}</Button>
                            </Grid2>
                            <Grid2>
                                <Button size='small' variant='outlined' onClick={() => setDeleteNode(null)} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>No</Button>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Box>
            </Modal>
        </Container>
    )
}

export default function SystemChatBot() {
    return (
        <ReactFlowProvider>
            <ReactFlowContainer />
        </ReactFlowProvider>
    )
}